import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Pencil, Trash2, Users, MapPin, UtensilsCrossed, 
  QrCode, RefreshCw, Copy, ExternalLink, Wifi, Clock,
  Settings2, Play
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function TableManagement({ user }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [generatingSession, setGeneratingSession] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [formData, setFormData] = useState({
    table_number: '',
    capacity: '4',
    location: '',
    rfid_tag: ''
  });

  const canManage = ['super_admin', 'hotel_admin', 'manager'].includes(user?.role);

  const locations = ['Indoor', 'Outdoor', 'Poolside', 'Rooftop', 'Private Dining', 'Bar Area'];

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      const response = await api.get('/tables');
      setTables(response.data);
    } catch (error) {
      toast.error('Failed to load tables');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingTable(null);
    setFormData({ table_number: '', capacity: '4', location: '', rfid_tag: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (table) => {
    setEditingTable(table);
    setFormData({
      table_number: table.table_number,
      capacity: table.capacity.toString(),
      location: table.location || '',
      rfid_tag: table.rfid_tag || ''
    });
    setDialogOpen(true);
  };

  const openQRDialog = (table) => {
    setSelectedTable(table);
    setCurrentSession(null);
    setQrDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTable) {
        await api.put(`/tables/${editingTable.id}`, {
          ...formData,
          capacity: parseInt(formData.capacity)
        });
        toast.success('Table updated');
      } else {
        await api.post('/tables', {
          ...formData,
          capacity: parseInt(formData.capacity)
        });
        toast.success('Table created');
      }
      loadTables();
      setDialogOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save table'));
    }
  };

  const deleteTable = async (tableId, tableName) => {
    if (!window.confirm(`Delete Table ${tableName}? This cannot be undone.`)) return;
    try {
      await api.delete(`/tables/${tableId}`);
      toast.success('Table deleted');
      loadTables();
    } catch (error) {
      toast.error('Failed to delete table');
    }
  };

  const updateStatus = async (tableId, status) => {
    try {
      await api.patch(`/tables/${tableId}/status?status=${status}`);
      toast.success('Table status updated');
      loadTables();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateRFID = async (tableId, rfidTag) => {
    try {
      await api.patch(`/tables/${tableId}/rfid?rfid_tag=${rfidTag}`);
      toast.success('RFID tag updated');
      loadTables();
    } catch (error) {
      toast.error('Failed to update RFID tag');
    }
  };

  const regenerateCode = async (tableId) => {
    try {
      const response = await api.post(`/tables/${tableId}/regenerate-code`);
      toast.success('QR code regenerated');
      loadTables();
      if (selectedTable?.id === tableId) {
        setSelectedTable(prev => ({
          ...prev,
          unique_code: response.data.unique_code,
          qr_code_url: response.data.qr_code_url
        }));
      }
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  const generateSession = async (tableId) => {
    setGeneratingSession(true);
    try {
      const response = await api.post(`/tables/${tableId}/generate-session`);
      setCurrentSession(response.data);
      toast.success('Session generated! Customer can now scan to order.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate session'));
    } finally {
      setGeneratingSession(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusBadge = (status) => {
    const styles = {
      available: 'bg-green-100 text-green-700',
      occupied: 'bg-red-100 text-red-700',
      reserved: 'bg-blue-100 text-blue-700',
      cleaning: 'bg-yellow-100 text-yellow-700',
      created: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  // Group tables by location
  const groupedTables = tables.reduce((acc, table) => {
    const loc = table.location || 'General';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(table);
    return acc;
  }, {});

  const stats = {
    total: tables.length,
    available: tables.filter(t => t.status === 'available').length,
    occupied: tables.filter(t => t.status === 'occupied').length,
    reserved: tables.filter(t => t.status === 'reserved').length
  };

  return (
    <div className="space-y-6" data-testid="table-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Table Management
          </h1>
          <p className="text-muted-foreground mt-1">Configure restaurant tables with QR self-ordering</p>
        </div>
        {canManage && (
          <Button onClick={openAddDialog} data-testid="add-table-button">
            <Plus className="h-4 w-4 mr-2" />
            Add Table
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Tables</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{stats.available}</p>
            <p className="text-sm text-muted-foreground">Available</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{stats.occupied}</p>
            <p className="text-sm text-muted-foreground">Occupied</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.reserved}</p>
            <p className="text-sm text-muted-foreground">Reserved</p>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTable ? 'Edit Table' : 'Add New Table'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="table_number">Table Number/Name</Label>
              <Input
                id="table_number"
                value={formData.table_number}
                onChange={(e) => setFormData({ ...formData, table_number: e.target.value })}
                placeholder="e.g., 1, A1, VIP-1"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity">Seating Capacity</Label>
              <Select
                value={formData.capacity}
                onValueChange={(v) => setFormData({ ...formData, capacity: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2, 4, 6, 8, 10, 12].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} seats</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={formData.location}
                onValueChange={(v) => setFormData({ ...formData, location: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rfid_tag">RFID/NFC Tag ID (Optional)</Label>
              <Input
                id="rfid_tag"
                value={formData.rfid_tag}
                onChange={(e) => setFormData({ ...formData, rfid_tag: e.target.value })}
                placeholder="For tap-to-order functionality"
              />
              <p className="text-xs text-muted-foreground">Enter the RFID tag ID for NFC-enabled ordering</p>
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1">
                {editingTable ? 'Update Table' : 'Add Table'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Table {selectedTable?.table_number} - QR Self-Order
            </DialogTitle>
            <DialogDescription>
              Generate and manage QR codes for customer self-ordering
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="static" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="static">Static QR</TabsTrigger>
              <TabsTrigger value="session">Session QR</TabsTrigger>
            </TabsList>
            
            <TabsContent value="static" className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  Permanent QR code for this table. Good for printed materials.
                </p>
                <div className="inline-block bg-white p-4 rounded-lg shadow-sm">
                  {selectedTable?.unique_code && (
                    <QRCode 
                      value={`${API_BASE_URL}/self-order/${selectedTable.unique_code}`}
                      size={180}
                    />
                  )}
                </div>
                <p className="text-xs mt-3 font-mono text-gray-500">
                  Code: {selectedTable?.unique_code}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => copyToClipboard(`${API_BASE_URL}/self-order/${selectedTable?.unique_code}`)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy URL
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => regenerateCode(selectedTable?.id)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </Button>
              </div>
              
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ Static QR requires self-ordering to be enabled in restaurant settings and won't expire.
              </p>
            </TabsContent>
            
            <TabsContent value="session" className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  One-time session QR. Expires after configured time or when order is placed.
                </p>
                
                {currentSession ? (
                  <>
                    <div className="inline-block bg-white p-4 rounded-lg shadow-sm">
                      <QRCode 
                        value={`${API_BASE_URL}${currentSession.order_url}`}
                        size={180}
                      />
                    </div>
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-mono text-gray-500">
                        Session: {currentSession.session?.session_code}
                      </p>
                      <div className="flex items-center justify-center gap-1 text-xs text-orange-600">
                        <Clock className="h-3 w-3" />
                        Expires: {new Date(currentSession.session?.expires_at).toLocaleTimeString()}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-8">
                    <QrCode className="h-16 w-16 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No active session</p>
                  </div>
                )}
              </div>
              
              <Button 
                className="w-full"
                onClick={() => generateSession(selectedTable?.id)}
                disabled={generatingSession}
              >
                {generatingSession ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    {currentSession ? 'Generate New Session' : 'Start Session'}
                  </>
                )}
              </Button>
              
              {currentSession && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => copyToClipboard(`${API_BASE_URL}${currentSession.order_url}`)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy URL
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`${API_BASE_URL}${currentSession.order_url}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                ✓ Session QR is more secure - expires after one order or timeout.
              </p>
            </TabsContent>
          </Tabs>
          
          {/* RFID Section */}
          {selectedTable?.rfid_tag && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Wifi className="h-4 w-4" />
                <span>RFID/NFC Tag: <code className="bg-gray-100 px-1 rounded">{selectedTable.rfid_tag}</code></span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tables Grid */}
      {loading ? (
        <div className="text-center py-12">Loading tables...</div>
      ) : tables.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UtensilsCrossed className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No tables configured</p>
            <p className="text-sm text-muted-foreground mt-2">Add tables to start managing your restaurant</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedTables).map(([location, locationTables]) => (
            <div key={location}>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {location}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {locationTables.map(table => (
                  <Card 
                    key={table.id} 
                    className={`relative ${table.status === 'occupied' ? 'border-red-300' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-lg">Table {table.table_number}</h4>
                        <Badge className={getStatusBadge(table.status)}>
                          {table.status}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                        <Users className="h-4 w-4" />
                        <span>{table.capacity} seats</span>
                      </div>
                      
                      {/* Unique Code Display */}
                      {table.unique_code && (
                        <div className="text-xs text-muted-foreground mb-2 font-mono bg-gray-50 px-2 py-1 rounded">
                          ID: {table.unique_code}
                        </div>
                      )}
                      
                      {/* RFID indicator */}
                      {table.rfid_tag && (
                        <div className="flex items-center gap-1 text-xs text-blue-600 mb-2">
                          <Wifi className="h-3 w-3" />
                          NFC Enabled
                        </div>
                      )}
                      
                      {canManage && (
                        <div className="space-y-2">
                          <Select
                            value={table.status}
                            onValueChange={(v) => updateStatus(table.id, v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="created">Created</SelectItem>
                              <SelectItem value="available">Available</SelectItem>
                              <SelectItem value="occupied">Occupied</SelectItem>
                              <SelectItem value="reserved">Reserved</SelectItem>
                              <SelectItem value="cleaning">Cleaning</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1"
                              onClick={() => openQRDialog(table)}
                            >
                              <QrCode className="h-3 w-3 mr-1" />
                              QR
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditDialog(table)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-600"
                              onClick={() => deleteTable(table.id, table.table_number)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
